import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Image as ImageIcon, Video, Calendar, Newspaper, Plus, Trash2, Edit2, Star, StarOff, Upload, Loader2, Eye, EyeOff, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = [
  { value: "gallery", label: "Photo (galerie)", icon: ImageIcon, color: "bg-blue-100 text-blue-700" },
  { value: "video", label: "Vidéo", icon: Video, color: "bg-purple-100 text-purple-700" },
  { value: "event", label: "Événement", icon: Calendar, color: "bg-orange-100 text-orange-700" },
  { value: "news", label: "Actualité", icon: Newspaper, color: "bg-green-100 text-green-700" },
  { value: "document", label: "Document officiel", icon: FileText, color: "bg-red-100 text-red-700" },
];

const typeConf = (t) => TYPES.find(x => x.value === t) || TYPES[0];

export default function SiteContentPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: "gallery", title: "", description: "", media_url: "", reference: "",
    event_date: "", published_from: "", published_until: "", location: "", is_featured: false
  });

  const load = () => {
    setLoading(true);
    api.get(`/site-content?include_expired=${showExpired}`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [showExpired]);

  const visible = filter === "all" ? items : items.filter(i => i.type === filter);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 20 Mo)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, media_url: data.url }));
      toast.success("Fichier envoyé");
    } catch { toast.error("Erreur d'envoi"); } finally { setUploading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ type: "gallery", title: "", description: "", media_url: "", reference: "", event_date: "", published_from: "", published_until: "", location: "", is_featured: false });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      type: item.type, title: item.title, description: item.description || "",
      media_url: item.media_url || "", reference: item.reference || "", event_date: item.event_date || "",
      published_from: item.published_from || "", published_until: item.published_until || "",
      location: item.location || "", is_featured: item.is_featured || false
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error("Titre requis");
    setSubmitting(true);
    try {
      if (editing) {
        await api.patch(`/site-content/${editing.id}`, form);
        toast.success("Contenu mis à jour");
      } else {
        await api.post("/site-content", form);
        toast.success("Contenu publié");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSubmitting(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer "${item.title}" définitivement ?`)) return;
    try {
      await api.delete(`/site-content/${item.id}`);
      toast.success("Contenu supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const isActive = (item) => {
    const now = new Date();
    if (item.published_from && new Date(item.published_from) > now) return false;
    if (item.published_until && new Date(item.published_until) < now) return false;
    return true;
  };

  return (
    <div className="space-y-5" data-testid="page-site-content">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="chip mb-2"><ImageIcon className="h-3 w-3" /><span>Alimentation du site</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Photos, vidéos, événements & actualités</h2>
          <p className="text-sm text-slate-500 mt-1">{items.length} contenu(s) · publiez ce qui s&apos;affiche sur le site public, avec dates de validité.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="sc-add-btn">
          <Plus className="h-4 w-4" /> Nouveau contenu
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-2 items-center">
        <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${filter === "all" ? "bg-[#1E3A8A] text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>Tout ({items.length})</button>
        {TYPES.map(t => {
          const count = items.filter(i => i.type === t.value).length;
          return (
            <button key={t.value} onClick={() => setFilter(t.value)} className={`text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 ${filter === t.value ? "bg-[#1E3A8A] text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`} data-testid={`sc-filter-${t.value}`}>
              <t.icon className="h-3 w-3" /> {t.label} ({count})
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowExpired(!showExpired)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" data-testid="sc-toggle-expired">
            {showExpired ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {showExpired ? "Inclure expirés" : "Actifs uniquement"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Aucun contenu publié.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map(item => {
            const conf = typeConf(item.type);
            const active = isActive(item);
            return (
              <div key={item.id} className={`bg-white border rounded-xl overflow-hidden ${active ? "border-slate-200" : "border-red-200 opacity-70"}`} data-testid={`sc-card-${item.id}`}>
                {item.media_url && (
                  <div className="aspect-video bg-slate-100 relative">
                    {item.type === "video" ? (
                      <video src={item.media_url} controls className="w-full h-full object-cover" />
                    ) : item.type === "document" ? (
                      <a href={item.media_url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-2 bg-red-50 hover:bg-red-100 transition-colors">
                        <FileText className="h-10 w-10 text-red-600" />
                        <span className="text-xs font-semibold text-red-700">Ouvrir le PDF</span>
                      </a>
                    ) : (
                      <img src={item.media_url} alt={item.title} className="w-full h-full object-cover" />
                    )}
                    {item.is_featured && (
                      <div className="absolute top-2 left-2 bg-[#FBBF24] text-[#0F172A] text-[10px] font-bold uppercase px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" /> Mise en avant
                      </div>
                    )}
                    {!active && (
                      <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-1 rounded-full">Expiré</div>
                    )}
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${conf.color}`}>
                      <conf.icon className="h-3 w-3 mr-1 inline" /> {conf.label}
                    </span>
                    {item.event_date && <span className="text-[10px] text-slate-500">📅 {item.event_date}</span>}
                  </div>
                  <h3 className="font-heading font-bold text-sm text-[#0F172A] mb-1">{item.title}</h3>
                  {item.reference && <p className="text-[10px] font-mono text-red-700 mb-1">N° {item.reference}</p>}
                  {item.description && <p className="text-xs text-slate-500 line-clamp-2 mb-2">{item.description}</p>}
                  {(item.published_from || item.published_until) && (
                    <p className="text-[10px] text-slate-400 mb-2">
                      Publication : {item.published_from?.slice(0, 10) || "immédiat"} → {item.published_until?.slice(0, 10) || "permanent"}
                    </p>
                  )}
                  <div className="flex gap-1.5 mt-3 border-t border-slate-100 pt-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(item)} className="text-xs gap-1 flex-1" data-testid={`sc-edit-${item.id}`}>
                      <Edit2 className="h-3 w-3" /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(item)} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`sc-delete-${item.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="sc-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau contenu"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="sc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sc-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="sc-desc" />
            </div>
            <div>
              <Label>{form.type === "document" ? "Fichier PDF" : "Image / Vidéo"}</Label>
              <div className="flex items-center gap-2 mt-1">
                <input id="sc-file" type="file" accept={form.type === "document" ? "application/pdf" : "image/*,video/*"} onChange={upload} className="hidden" data-testid="sc-file" />
                <label htmlFor="sc-file" className="cursor-pointer text-xs px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center gap-1">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {form.media_url ? "Remplacer" : "Téléverser"}
                </label>
                {form.media_url && (form.type === "video" ? <Video className="h-5 w-5 text-purple-600" /> : form.type === "document" ? <FileText className="h-5 w-5 text-red-600" /> : <img src={form.media_url} alt="" className="w-10 h-10 rounded object-cover" />)}
              </div>
            </div>
            {form.type === "document" && (
              <div>
                <Label>Numéro / Référence officielle</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Ex : A/2022/265/MEPU-A/SGG" data-testid="sc-reference" />
              </div>
            )}
            {form.type === "event" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date de l&apos;événement</Label>
                  <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="sc-event-date" />
                </div>
                <div>
                  <Label>Lieu</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="sc-location" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Publier à partir du</Label>
                <Input type="datetime-local" value={form.published_from?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, published_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="sc-from" />
              </div>
              <div>
                <Label>Expire le</Label>
                <Input type="datetime-local" value={form.published_until?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, published_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="sc-until" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="rounded" data-testid="sc-featured" />
              <Star className="h-3 w-3 text-[#FBBF24] fill-current" /> Mettre en avant sur la page d&apos;accueil
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={submitting} className="bg-[#EA580C] hover:bg-orange-700 text-white" data-testid="sc-save">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
